import { X } from "lucide-react";
import { useEffect } from "react";
import { IssueChip } from "../../web/components/ui";
import type { ItemProblem, ItemProblemId } from "./attention";

/*
 * A window listing items (a bundle's contents, a group's items, and any list like them): what needs attention on its items, the
 * same everywhere. Along the top of its "Needs attention" box, a chip for each kind of warning its items have, with how many ("2 Price
 * to update"; which, on hover); pressing one narrows its list to those items (`only`), said in the list's header with a pill that
 * clears it (OnlyPill). Once none has it any more, the whole list shows again (useLiveOnly). The items' warnings are attention.ts's
 * (listAttention: every one they have), so nothing's missed.
 */

/** One kind of warning among a list's items: its few words, red if any of them is, and which items (by name). */
export interface ProblemKind {
    id: ItemProblemId;
    text: string;
    danger: boolean;
    names: string[];
}

/** A warning's few words, without what's in brackets ("No cost (its pricing needs one)": "No cost"), for a chip. */
export const shortProblem = (text: string) => text.replace(/ ?\([^()]*\)$/, "");

/** Its items' warnings by kind, in the order they first come (each item's are worst first). */
export function problemKinds(problems: ReadonlyMap<string, readonly ItemProblem[]>, nameOf: (uuid: string) => string): ProblemKind[] {
    const kinds = new Map<ItemProblemId, ProblemKind>();
    for (const [uuid, list] of problems) {
        for (const p of list) {
            const kind = kinds.get(p.id) ?? { id: p.id, text: shortProblem(p.text), danger: false, names: [] };
            kind.danger ||= p.danger;
            kind.names.push(nameOf(uuid));
            kinds.set(p.id, kind);
        }
    }
    return [...kinds.values()];
}

/**
 * The kind of warning a list is narrowed to while any of its items still has it: once none does (all dealt with), it's cleared
 * (`onOnly(null)`) and the whole list shows again. What the list should go by (null meanwhile too).
 */
export function useLiveOnly(only: ItemProblemId | null, problems: ReadonlyMap<string, readonly ItemProblem[]>, onOnly: (only: ItemProblemId | null) => void) {
    const live = only !== null && [...problems.values()].some((list) => list.some((p) => p.id === only)) ? only : null;
    useEffect(() => {
        if (only !== null && live === null) onOnly(null);
    }, [only, live, onOnly]);
    return live;
}

/** Whether an item is listed, its list narrowed to one kind of warning (`only`; null: every item). */
export const withProblem = (problems: ReadonlyMap<string, readonly ItemProblem[]>, uuid: string, only: ItemProblemId | null) =>
    only === null || (problems.get(uuid) ?? []).some((p) => p.id === only);

/** A chip for each kind ("2 Price to update", red if any is; which items on hover): pressing it narrows the list to them (`onShow`). */
export function ProblemChips({ kinds, onShow }: Readonly<{ kinds: readonly ProblemKind[]; onShow: (kind: ItemProblemId) => void }>) {
    return (
        <>
            {kinds.map((k) => (
                <IssueChip key={k.id} tone={k.danger ? "error" : "warning"} label={`${k.names.length.toLocaleString()} ${k.text}`} title={k.names.join(", ")} onShow={() => onShow(k.id)} />
            ))}
        </>
    );
}

/** In a list's header while it's narrowed to one kind of warning (useLiveOnly): which (its × shows every item again). */
export function OnlyPill({ only, kinds, onClear }: Readonly<{ only: ItemProblemId | null; kinds: readonly ProblemKind[]; onClear: () => void }>) {
    const kind = kinds.find((k) => k.id === only);
    if (!kind) return null;
    return (
        <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-tint px-2 py-0.5 whitespace-nowrap text-warning-ink"
            title="Showing those with this warning: show every item"
            onClick={onClear}
        >
            {kind.text} <X className="size-3" />
        </button>
    );
}
